import {
  InputTargetNode,
  MessagingTopology,
  NodeBinding,
  WaitQueueNode,
} from '@internal/open-catalog';

export const isDelayedQueueFeatureEnabled = (
  topology: MessagingTopology,
): boolean => {
  return topology.delayedQueue !== undefined;
};

export const isStreamQueueEnabled = (topology: MessagingTopology): boolean => {
  return (
    topology.input?.targets.find(item => item.type === 'stream') !== undefined
  );
};

export const buildDelayedQueue = (
  topology: MessagingTopology,
): MessagingTopology => {
  topology.delayedQueue = {
    type: 'quorum',
    name: `${topology.channelName}.delayed`,
    arguments: {},
  };

  return topology;
};

export const buildStreamQueue = (
  topology: MessagingTopology,
): MessagingTopology => {
  const streamQueue = {
    type: 'stream',
    name: `${topology.channelName}-input.stream`,
    arguments: {},
    bindings: [
      {
        type: 'queue',
        source: `${topology.channelName}-input`,
        arguments: {
          subject: 'service-stream',
        },
      },
    ],
  } as InputTargetNode;

  topology.input?.targets.push(streamQueue);
  return topology;
};

export const removeStreamQueue = (
  topology: MessagingTopology,
): MessagingTopology => {
  topology.input?.targets.forEach((item, index) => {
    if (item.type === 'stream') {
      topology.input?.targets.splice(index, 1);
    }
  });
  return topology;
};

export const removeDelayedQueue = (
  topology: MessagingTopology,
): MessagingTopology => {
  topology.delayedQueue = undefined;
  return topology;
};

export const isRacPresetEnabled = (topology: MessagingTopology): boolean => {
  return (
    topology.input?.sources.find(i => i.name === 'rac-output') !== undefined &&
    topology.input?.targets.find(i => i.name.endsWith('.rac')) !== undefined
  );
};

const buildQueue = (
  channelName: string,
  queueName: string,
  bindings: NodeBinding[],
): InputTargetNode => {
  const name = `${channelName}.${queueName}`;
  return {
    name,
    type: 'quorum',
    bindings,
    arguments: {
      'x-dead-letter-exchange': '',
      'x-dead-letter-routing-key': `${name}.wait`,
      'x-queue-type': 'quorum',
    },
    wait: {
      name: `${name}.wait`,
      type: 'quorum',
      arguments: {
        'x-dead-letter-exchange': '',
        'x-dead-letter-routing-key': name,
        'x-queue-type': 'quorum',
        'x-message-ttl': 1000,
      },
    } as WaitQueueNode,
  } as InputTargetNode;
};

export const buildRacPreset = (
  topology: MessagingTopology,
): MessagingTopology => {
  const inputChannel = topology.input?.name || '';

  topology.input?.sources.push({
    name: 'rac-output',
    type: 'headers',
    bindings: [
      {
        type: 'exchange',
        source: 'rac-output',
        arguments: {},
        routingKey: '',
      },
    ],
    arguments: {},
  });

  topology.input?.targets.push(
    buildQueue(inputChannel, 'rac', [
      {
        type: 'queue',
        source: inputChannel,
        arguments: { source: 'rac' },
      },
    ]),
  );

  return topology;
};

export const removeRacPreset = (
  topology: MessagingTopology,
): MessagingTopology => {
  const inputChannel = topology.input?.name || '';

  topology.input?.sources.forEach((item, index) => {
    if (item.name === 'rac-output') {
      topology.input?.sources.splice(index, 1);
    }
  });

  topology.input?.targets.forEach((item, index) => {
    if (item.name === `${inputChannel}.rac`) {
      topology.input?.targets.splice(index, 1);
    }
  });

  return topology;
};

export const isSgdpPresetEnabled = (topology: MessagingTopology): boolean => {
  return false;
};

export const buildSgdpPreset = (
  topology: MessagingTopology,
): MessagingTopology => {
  return topology;
};

export const removeSgdpPreset = (
  topology: MessagingTopology,
): MessagingTopology => {
  return topology;
};

export const isSmartTalkPresetEnabled = (
  topology: MessagingTopology,
): boolean => {
  return (
    topology.input?.sources.find(i => i.name === 'smart-talk-output') !==
      undefined &&
    topology.input?.targets.find(i => i.name.endsWith('.notifications')) !==
      undefined &&
    topology.output?.targets.find(i => i.name === 'smart-talk-input') !==
      undefined
  );
};

export const buildSmartTalkPreset = (
  topology: MessagingTopology,
  applicationId: string,
): MessagingTopology => {
  const inputChannel = topology.input?.name || '';
  const outputChannel = topology.output?.name || '';

  const smartTalkInput = 'smart-talk-input';
  const smartTalkOutput = 'smart-talk-output';

  topology.input?.sources.push({
    name: smartTalkOutput,
    type: 'headers',
    bindings: [
      {
        type: 'exchange',
        source: smartTalkOutput,
        arguments: { applicationId: applicationId },
        routingKey: '',
      },
    ],
    arguments: {},
  });

  topology.input?.targets.push(
    buildQueue(inputChannel, 'notifications', [
      {
        type: 'queue',
        source: inputChannel,
        arguments: { source: 'smart-talk' },
      },
    ]),
  );

  topology.output?.targets.push({
    name: smartTalkInput,
    type: 'headers',
    bindings: [
      {
        type: 'exchange',
        source: outputChannel,
        arguments: { target: 'smart-talk' },
        routingKey: '',
      },
    ],
    arguments: {},
  });

  return topology;
};

export const removeSmartTalkPreset = (
  topology: MessagingTopology,
): MessagingTopology => {
  const channelName = topology.input?.name || '';

  topology.input?.sources.forEach((item, index) => {
    if (item.name === 'smart-talk-output') {
      topology.input?.sources.splice(index, 1);
    }
  });

  topology.input?.targets.forEach((item, index) => {
    if (item.name === `${channelName}.notifications`) {
      topology.input?.targets.splice(index, 1);
    }
  });

  topology.output?.targets.forEach((item, index) => {
    if (item.name === 'smart-talk-input') {
      topology.output?.targets.splice(index, 1);
    }
  });

  return topology;
};

export const isSchedulerPresetEnabled = (
  topology: MessagingTopology,
): boolean => {
  return (
    topology.input?.sources.find(i => i.name === 'scheduler-output') !==
      undefined &&
    topology.input?.targets.find(i => i.name.endsWith('.scheduled-tasks')) !==
      undefined &&
    topology.output?.targets.find(i => i.name === 'scheduler-input') !==
      undefined
  );
};

export const buildSchedulerPreset = (
  topology: MessagingTopology,
): MessagingTopology => {
  const service = topology.channelName;
  const inputChannel = topology.input?.name || '';
  const outputChannel = topology.output?.name || '';

  const schedulerInput = 'scheduler-input';
  const schedulerOutput = 'scheduler-output';

  topology.input?.sources.push({
    name: schedulerOutput,
    type: 'headers',
    bindings: [
      {
        type: 'exchange',
        source: schedulerOutput,
        arguments: { target: service },
        routingKey: '',
      },
    ],
    arguments: {},
  });

  topology.input?.targets.push(
    buildQueue(inputChannel, 'scheduled-tasks', [
      {
        type: 'queue',
        source: inputChannel,
        arguments: { source: 'scheduler' },
      },
    ]),
  );

  topology.output?.targets.push({
    name: schedulerInput,
    type: 'headers',
    bindings: [
      {
        type: 'exchange',
        source: outputChannel,
        arguments: { target: 'scheduler' },
        routingKey: '',
      },
    ],
    arguments: {},
  });

  return topology;
};

export const removeSchedulerPreset = (
  topology: MessagingTopology,
): MessagingTopology => {
  const channelName = topology.input?.name || '';

  topology.input?.sources.forEach((item, index) => {
    if (item.name === 'scheduler-output') {
      topology.input?.sources.splice(index, 1);
    }
  });

  topology.input?.targets.forEach((item, index) => {
    if (item.name === `${channelName}.scheduled-tasks`) {
      topology.input?.targets.splice(index, 1);
    }
  });

  topology.output?.targets.forEach((item, index) => {
    if (item.name === 'scheduler-input') {
      topology.output?.targets.splice(index, 1);
    }
  });

  return topology;
};

export const isAccessRequestsEnabled = (
  topology: MessagingTopology,
): boolean => {
  return (
    topology.input?.sources.find(i => i.name === 'se-access-output') !==
      undefined &&
    topology.input?.targets.find(i => i.name.endsWith('.access-requests')) !==
      undefined &&
    topology.output?.targets.find(i => i.name === 'se-access-input') !==
      undefined
  );
};

export const buildAccessRequestsPreset = (
  topology: MessagingTopology,
): MessagingTopology => {
  const service = topology.channelName;
  const inputChannel = topology.input?.name || '';
  const outputChannel = topology.output?.name || '';

  const seAccessInput = 'se-access-input';
  const seAccessOutput = 'se-access-output';

  topology.input?.sources.push({
    name: seAccessOutput,
    type: 'headers',
    bindings: [
      {
        type: 'exchange',
        source: seAccessOutput,
        arguments: { target: service },
        routingKey: '',
      },
    ],
    arguments: {},
  });

  topology.input?.targets.push(
    buildQueue(inputChannel, 'access-requests', [
      {
        type: 'queue',
        source: inputChannel,
        arguments: { source: 'se-access', subject: 'access-request' },
      },
    ]),
  );

  topology.output?.targets.push({
    name: seAccessInput,
    type: 'headers',
    bindings: [
      {
        type: 'exchange',
        source: outputChannel,
        arguments: { target: 'se-access' },
        routingKey: '',
      },
    ],
    arguments: {},
  });

  return topology;
};

export const removeAccessRequestsPreset = (
  topology: MessagingTopology,
): MessagingTopology => {
  const channelName = topology.input?.name || '';

  topology.input?.sources.forEach((item, index) => {
    if (item.name === 'se-access-output') {
      topology.input?.sources.splice(index, 1);
    }
  });

  topology.input?.targets.forEach((item, index) => {
    if (item.name === `${channelName}.access-requests`) {
      topology.input?.targets.splice(index, 1);
    }
  });

  topology.output?.targets.forEach((item, index) => {
    if (item.name === 'se-access-input') {
      topology.output?.targets.splice(index, 1);
    }
  });

  return topology;
};
